<template>
  <AppEventContainer feature="Leaderboard">

    <AppScreenTitle title="Leaderboard" tool-tip-key="Leaderboard"></AppScreenTitle>
    <LeaderboardEventHeader :poker-run-event="currentPokerRunEvent" :total-participants="totalParticipants"
      :total-hands="leaderboard.length"></LeaderboardEventHeader>

    <!-- Search bar -->
    <div class="relative">

      <AppInputText name="search" placeholder="Search participants" :required="false"
        @update:modelValue="handleSearchTextChange">
        <template #prefix>
          <i class="fas fa-search text-normal-reduced"></i>
        </template>
      </AppInputText>
    </div>

    <!-- Controls row -->
    <div class="flex justify-between items-center mb-4">
      <!-- Expand/Collapse all -->
      <div>
        <AppExpandCollapse v-model="allExpanded" @update:model-value="(value: boolean) => allExpanded = value" />
      </div>

      <!-- View options -->
      <div v-if="showFilters" class="relative w-[150px]">
        <AppSwitch label="View mine only" v-model="viewMine" @update:model-value="(value: boolean) => setLeaderboardViewMine(value)" />
      </div>
    </div>

    <!-- Leaderboard table -->
    <div class="bg-white rounded-lg overflow-hidden">
      <!-- Table header -->
      <div class="grid grid-cols-12 gap-2 text-normal-emphasis text-sm font-bold border-b border-border-medium">
        <div class="col-span-2 text-right">Rank</div>
        <div class="col-span-5">Participant</div>
        <div class="col-span-4 text-right">Hand</div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="flex justify-center items-center pt-4">
        <AppLoading :loading="loading"></AppLoading>
      </div>

      <!-- Table rows -->
      <div v-else>
        <template v-for="(item, index) in filteredLeaderboard" :key="`rank-${eventId}-${item.position}-${item.participantId}-${viewMode}`">
          <LeaderboardListItem :item="item" :show-hand="allExpanded"></LeaderboardListItem>
        </template>

        <!-- Empty state messages -->
        <div v-if="filteredLeaderboard.length === 0" class="text-center py-4 text-normal-reduced">
          <template v-if="searchQuery">
            No participants match your search
          </template>
          <template v-else-if="viewMine && leaderboard.length > 0">
            You don't have any hands on the leaderboard
          </template>
          <template v-else>
            No participants on the leaderboard yet
          </template>
        </div>
      </div>
    </div>
  </AppEventContainer>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useEventParticipantStore } from "@/store/EventParticipant";
import { useUserProfileStore } from "@/store/UserProfile";
import LeaderboardListItem from "@/components/LeaderboardListItem.vue";
import useLeaderboard from "@/modules/useLeaderboard";
import { storeToRefs } from "pinia";
import EventParticipantDataService from "@/services/EventParticipantDataService";
import LeaderboardEventHeader from "@/components/LeaderboardEventHeader.vue";
import OptionSelect from "@/components/OptionSelect.vue";
import AppSwitch from "@/components/AppSwitch.vue";

const userProfileStore = useUserProfileStore();
const { profile, leaderboardViewMine } = storeToRefs(userProfileStore);
const {setLeaderboardViewMine } = userProfileStore;
const { leaderboard, getLeaderboard, totalParticipants, unsubscribeLeaderboard } = useLeaderboard();
const { currentPokerRunEvent, currentEventParticipants } = storeToRefs(
  useEventParticipantStore()
);
const { loadEventParticipant } = useEventParticipantStore();
const route = useRoute();
const message = ref("");
const messageDetails = ref("");
const searchQuery = ref("");
const showFilters = ref(false);
const viewMine = ref(leaderboardViewMine.value);

// This computed property is no longer needed as we're using the filteredLeaderboard with viewMode

const hasRankedHand = computed(() => {
  return leaderboard.value.some(x => x.rank.cardCount > 0);
});

// Filtered leaderboard based on search query and view mode
const filteredLeaderboard = computed(() => {
  // Start with the base leaderboard
  let filtered = leaderboard.value;

  // Filter by view mode
  if (viewMine.value) {
    // Get the participant IDs for the current user
    const participantIds = currentEventParticipants.value?.map(x => x.id) || [];
    filtered = filtered.filter(item => participantIds.includes(item.participantId));
  }

  // Then filter by search query if present
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(item => {
      return item.participantName.toLowerCase().includes(query);
    });
  }

  return filtered;
});

// Metrics are now handled by LeaderboardEventHeader component

// eslint-disable-next-line operator-linebreak
const eventId = route.params.id
  ? route.params.id
  : currentPokerRunEvent.value
    ? currentPokerRunEvent.value.id
    : null;

const selectedParticipant = ref();
const loading = ref(true);
const allExpanded = ref(false);

// Hand names are now handled by LeaderboardListItem component

const handleSearchTextChange = (text: string) => {
  searchQuery.value = text;
};

const initalize = async () => {
  try {
    if (!eventId) return;

    await loadEventParticipant(eventId as string, profile.value.id);
    const eventParticipants = await EventParticipantDataService.get(eventId as string, profile.value.id);
    const participantIds = eventParticipants.map((x) => x.id as string);
    getLeaderboard(eventId as string, participantIds);
    if(eventParticipants.length > 1) {
      showFilters.value = true;
    }
  } catch (error: any) {
    message.value = "Error loading tracker.";
    messageDetails.value = error.message || "Unknown error";
  } finally {
    loading.value = false;
  }
};

onUnmounted(unsubscribeLeaderboard);
initalize();
</script>